import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete, TextField, CircularProgress, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { useSearch } from '../hooks/useSearch';
import { useAuth } from '../context/AuthContext';

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  minWidth: 0, // Allow shrinking
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0px 1px 3px rgba(0, 0, 0, 0.5)' 
    : '0px 1px 3px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.up('sm')]: {
    width: 400,
    maxWidth: 400,
  },
  [theme.breakpoints.up('md')]: {
    width: 500,
    maxWidth: 500,
  },
  [theme.breakpoints.up('lg')]: {
    width: 600,
    maxWidth: 600,
  },
  '& .MuiInputBase-root': {
    color: theme.palette.text.primary,
    padding: '2px 8px',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
    '& input::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 0.7,
    },
  },
  '& .MuiAutocomplete-popupIndicator': {
    display: 'none'
  },
  '& .MuiAutocomplete-clearIndicator': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-root': {
    padding: '4px 8px',
  },
  '& .MuiAutocomplete-option': {
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(0, 0, 0, 0.04)',
    },
    '&[aria-selected="true"]': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(52, 152, 219, 0.2)' 
        : 'rgba(52, 152, 219, 0.1)',
    },
  },
  // Make dropdown menu match the width of the input
  '& .MuiAutocomplete-popper': {
    width: '100% !important',
    maxWidth: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '400px !important',
      maxWidth: '400px',
    },
    [theme.breakpoints.up('md')]: {
      width: '500px !important',
      maxWidth: '500px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '600px !important',
      maxWidth: '600px',
    },
    '& .MuiPaper-root': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      maxHeight: 'calc(50vh - 100px)', // Limit height on mobile to fit in dialog
      [theme.breakpoints.up('sm')]: {
        maxHeight: '300px', // Standard height on larger screens
      },
    },
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.text.secondary,
  marginRight: '8px',
}));

const StyledChip = styled(Chip)(({ theme, role }) => ({
  marginLeft: theme.spacing(1),
  height: '24px',
  ...(role === 'student' && {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  }),
  ...(role === 'teacher' && {
    backgroundColor: theme.palette.secondary.main,
    color: '#fff',
  }),
  ...(role === 'guardian' && {
    backgroundColor: theme.palette.success.main,
    color: '#fff',
  }),
}));

const Search = ({ onSelect }) => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const { searchResults, loading, searchUsers } = useSearch();

  // Show search for admin/superadmin and teachers
  if (!['admin', 'superadmin', 'teacher'].includes(userRole)) {
    return null;
  }

  const handleInputChange = (event, value) => {
    searchUsers(value);
  };

  const handleOptionSelect = (event, option) => {
    if (!option) return;

    // Navigate to the appropriate detail page based on role
    switch (option.role) {
      case 'student':
        navigate(`/student_details/${option.id}`);
        break;
      case 'teacher':
        navigate(`/teacher_details/${option.id}`);
        break;
      case 'guardian':
        navigate(`/parent_details/${option.id}`);
        break;
      default:
        console.warn('Unknown role:', option.role);
    }
    
    // Call onSelect callback if provided (for closing mobile dialog)
    if (onSelect) {
      onSelect();
    }
  };

  return (
    <StyledAutocomplete
      options={searchResults}
      loading={loading}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      getOptionLabel={(option) => option.label || ''}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={(props, option) => (
        <li {...props}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
            <span>{`${option.label.split(' (')[0]}`}</span>
            <StyledChip 
              label={option.role.charAt(0).toUpperCase() + option.role.slice(1)} 
              role={option.role}
              size="small"
            />
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search..."
          variant="outlined"
          size="small"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <SearchIconWrapper>
                <SearchIcon fontSize="small" />
              </SearchIconWrapper>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress color="secondary" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default Search; 